import { GlobalStyles } from "@mui/material";
import { CSSProperties } from "react";

/** Styles for .rbc-event */
export const rbcEventStyles: CSSProperties = {
  boxShadow: "none",
  border: "none",
  borderRadius: "10px",
  minHeight: 28,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  overflow: "hidden",
} as const;

/** Styles for .rbc-event-content */
export const rbcEventContentStyles: CSSProperties = {
  width: "100%",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
} as const;

/** Global style overrides for react-big-calendar.
 * Use static constant, to avoid rerendering. */
export const rbcGlobalStyles = (
  <GlobalStyles
    styles={(_theme) => {
      const isDark = _theme.palette.mode === "dark";
      return {
        ".custom-rbc-year-view .rbc-calendar": {
          ".rbc-month-view, .rbc-time-view, .rbc-agenda-view": {
            minHeight: "unset",
          },
        },
        ".rbc-calendar": {
          background: "transparent",
          borderRadius: "10px",
          padding: 0,
        },
        ".rbc-month-view, .rbc-time-view, .rbc-agenda-view": {
          minHeight: "1000px",
          border: "none",
          background: "transparent",
        },
        ".rbc-month-header": {
          border: "none",
        },
        ".rbc-header": {
          borderRadius: "10px",
          border: `1px solid ${_theme.palette.divider}`,
          background: isDark ? _theme.palette.background.paper : "transparent",
          color: isDark ? _theme.palette.text.primary : "#363636",
          fontWeight: 500,
          fontSize: 14,
          lineHeight: 1,
          textAlign: "end",
          padding: "6px",
          margin: "2px",
        },
        ".rbc-row": {
          border: "none",
        },
        ".rbc-month-row, .rbc-row-bg": {
          border: "none",
        },
        ".rbc-month-row + .rbc-month-row": {
          borderTop: "none",
        },
        ".rbc-month-view > .rbc-month-header > .rbc-header + .rbc-header": {
          borderLeft: `1px solid ${_theme.palette.divider}`,
        },
        ".rbc-month-view > .rbc-month-row > .rbc-row-bg > .rbc-day-bg + .rbc-day-bg": {
          borderLeft: `1px solid ${_theme.palette.divider}`,
        },
        ".rbc-day-bg": {
          background: isDark ? _theme.palette.background.paper : "#fff",
          borderRadius: "10px",
          border: `1px solid ${_theme.palette.divider}`,
          boxSizing: "border-box",
          margin: 2,
          overflow: "hidden",
        },
        ".rbc-off-range-bg": {
          background: isDark ? _theme.palette.background.default : "#F8FAFB",
        },
        ".rbc-date-cell": {
          textAlign: "right",
          padding: 8,
          fontWeight: 500,
          fontSize: 14,
          color: isDark ? _theme.palette.text.secondary : "#A3A9B6",
          border: "none",
        },
        ".rbc-event": rbcEventStyles,
        // Remove shadow from events in agenda view
        ".rbc-agenda-view tr": {
          boxShadow: "none !important",
        },
        ".rbc-event-content": rbcEventContentStyles,
        ".rbc-row-segment": {
          padding: "2px 10px",
        },
        ".rbc-row-segment > .rbc-show-more": {
          width: "-webkit-fill-available",
          textAlign: "start",
          minHeight: 30,
          color: _theme.palette.text.main,
        },
        ".rbc-row-segment > .rbc-show-more:hover": {
          color: _theme.palette.text.main,
        },
        ".rbc-show-more": {
          background: isDark ? _theme.palette.background.paper : "#fff",
          border: `1px solid ${_theme.palette.divider}`,
          borderRadius: "10px",
          color: isDark ? _theme.palette.text.secondary : "#A3A9B6",
          fontWeight: 500,
          fontSize: 14,
          padding: "2px 8px",
          cursor: "pointer",
        },
        ".rbc-today": {
          background: "transparent",
        },
        ".rbc-time-view > .rbc-time-header > .rbc-time-header-content > .rbc-time-header-cell > .rbc-header":
          {
            borderLeft: "none",
            borderRight: "none",
            borderTop: "none",
            borderRadius: 0,
            margin: 0,
            padding: 2,
          },
        ".rbc-time-view > .rbc-time-header > .rbc-time-header-content > .rbc-time-header-cell > .rbc-header + .rbc-header":
          {
            borderLeft: `1px solid ${_theme.palette.divider}`,
          },
        ".rbc-time-view > .rbc-time-header > .rbc-time-header-content": {
          borderLeft: `1px solid ${_theme.palette.divider}`,
        },
        ".rbc-time-view > .rbc-time-content > .rbc-time-gutter rbc-time-column > .rbc-timeslot-group":
          {
            borderBottom: `1px solid ${_theme.palette.divider}`,
          },
        ".rbc-time-view > .rbc-time-content > .rbc-time-column > .rbc-timeslot-group": {
          borderLeft: `1px solid ${_theme.palette.divider}`,
          borderBottom: `1px solid ${_theme.palette.divider}`,
        },
        ".rbc-time-view > .rbc-time-content > .rbc-time-column > .rbc-timeslot-group > .rbc-time-slot":
          {
            borderTop: "none",
          },
        ".rbc-time-view > .rbc-time-content > .rbc-time-column > .rbc-events-container": {
          borderLeft: `1px solid ${_theme.palette.divider}`,
        },
        ".rbc-time-view > .rbc-time-header > .rbc-time-header-content > .rbc-allday-cell > .rbc-row-bg > .rbc-day-bg":
          {
            margin: 0,
            border: "none",
            borderRadius: 0,
          },
        ".rbc-time-view > .rbc-time-header > .rbc-time-header-content > .rbc-allday-cell > .rbc-row-bg > .rbc-day-bg + .rbc-day-bg":
          {
            borderLeft: `1px solid ${_theme.palette.divider}`,
          },
        ".rbc-time-view .rbc-time-content": {
          borderTop: `1px solid ${_theme.palette.divider}`,
        },
        ".rbc-overlay": {
          borderRadius: "10px",
          background: _theme.palette.background.paper,
          border: `1px solid ${_theme.palette.divider}`,
        },
        ".rbc-overlay-header": {
          borderBottom: `1px solid ${_theme.palette.divider}`,
        },
      };
    }}
  />
);
